// axios.js
import axios from "axios";
import router from "@/router";
import Swal from "sweetalert2";

// 🔒 controle de sessão
let isLoggingOut = false;
let isRedirectingToLogin = false;

// 🧠 cache de requisições GET
const cache = new Map();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutos
const CACHE_DURATION_MS_MIN = 20 * 1000; // 20 segundos

// ❗ confira se sua env é a do API_URL; VUE_APP_TITLE costuma ser só o título do app
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || process.env.VUE_APP_TITLE,
  headers: { "Content-Type": "application/json" },
  withCredentials: false, // coloque true se usa cookies httpOnly para refresh
});

// 🔕 controle de cancelamento (AbortController por request)
const controllers = new Set();
function cancelAllRequests() {
  for (const c of controllers) c.abort();
  controllers.clear();
}

// Interceptor de requisição
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    const rotasLiberadas = [
      "/consulta",
      "/register",
      "/login",
      "/declaracao/assinaturas",
      "/declaracao/assinar",
      "/login/logoff", // ✅ permitir chamar logout autenticado
      "/background-config", // ✅ configuração de background (sem auth)
      "/assessor",
    ];
    const rotasSemCache = [
      "/register",
      "/login",
      "/excluir",
      "/declaracao/assinante",
      "/produto",
      "/suitability",
      "/produtos",
      "/cliente/ConsultaLembreteVencimento",
      "/declaracao/assinaturas/consultarDeclaracaoParaAssinatura",
      "/declaracao/existeDeclaracaoAssinaturaCliente",
      "/declaracao",
      "/documento/listarDocumentos",
      "/background-config",
      "/PessoaJuridica/listarSocios", // ✅ sempre buscar configuração atualizada
      "/PessoaJuridica/listarRepresentantes", // ✅ sempre buscar configuração atualizada
    ];
    const rotasCacheReduzido = ["/declaracao/cliente"];

    const url = (config.url || "").toLowerCase();

    const isRotaLiberada = rotasLiberadas.some((n) =>
      url.includes(n.toLowerCase()),
    );
    const isRotaSemCache = rotasSemCache.some((n) =>
      url.includes(n.toLowerCase()),
    );
    const isRotaCacheReduzido = rotasCacheReduzido.some((n) =>
      url.includes(n.toLowerCase()),
    );

    // 🔄 limpando cache ao entrar no login
    // eslint-disable-next-line no-debugger
    if (router.currentRoute.name === "UserLogin" || url.includes("/login")) {
      cache.clear();
    }

    // 🧠 Caching de GET (corrigido o “cache reduzido”)
    if (config.method === "get") {
      const cached = cache.get(config.url);
      const age = cached ? Date.now() - cached.timestamp : Infinity;
      const ttl = isRotaCacheReduzido
        ? CACHE_DURATION_MS_MIN
        : CACHE_DURATION_MS;

      if (!isRotaSemCache && cached && age < ttl) {
        return Promise.reject({ __fromCache: true, data: cached.response });
      } else if (cached && age >= ttl) {
        cache.delete(config.url);
      }
    }

    // 🧽 invalidação de cache para POST e GET de exclusão
    if (
      config.method === "post" ||
      (config.method === "get" && url.includes("excluir"))
    ) {
      const urlParts = config.url.split("/").filter(Boolean);
      const secondWord = urlParts[1];
      for (const key of Array.from(cache.keys())) {
        if (
          key.startsWith(config.url) ||
          (secondWord && key.includes(secondWord))
        ) {
          cache.delete(key);
        }
      }
    }

    // 🛑 token opcional nas rotas liberadas
    if (isRotaLiberada) {
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } else {
      // 🔐 exige token nas demais
      if (!token) {
        return Promise.reject({
          config,
          response: {
            status: 401,
            data: { message: "Token ausente. A requisição foi bloqueada." },
          },
          isTokenMissing: true,
        });
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 🛟 adicionar AbortController por request
    const controller = new AbortController();
    config.signal = controller.signal;
    controllers.add(controller);

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor de resposta
api.interceptors.response.use(
  (response) => {
    // 🧠 cache de GET
    if (response.config.method === "get") {
      cache.set(response.config.url, { response, timestamp: Date.now() });
    }
    return response;
  },
  (error) => {
    // limpando controller finalizado
    if (error.config?.signal) {
      // o axios/AbortController já cancela; controllers serão limpos no cancelAllRequests()
    }

    // 🧠 Se veio do cache, devolve resolvido
    if (error.__fromCache) return Promise.resolve(error.data);

    // Ignora erro criado localmente por falta de token (não spam)
    if (error.isTokenMissing) {
      return new Promise(() => {}); // pendura silenciosamente
    }

    const url = (error.config?.url || "").toLowerCase();
    const rotasSemTratamentoDeErroGenerico = [
      "/register",
      "/login",
      "/declaracoes",
    ];
    const isSemTratGen = rotasSemTratamentoDeErroGenerico.some((n) =>
      url.includes(n.toLowerCase()),
    );

    if (error.response && error.response.status === 401) {
      // Se estamos executando logout, só propaga o erro sem redirecionar
      if (isLoggingOut) return Promise.reject(error);

      if (!isRedirectingToLogin && router.currentRoute.name !== "UserLogin") {
        isRedirectingToLogin = true;

        router.push({ name: "UserLogin" }).finally(() => {
          // libera futuros redirects após a navegação
          setTimeout(() => {
            isRedirectingToLogin = false;
          }, 300);
        });

        Swal.fire({
          icon: "warning",
          title: "Sessão expirada",
          text: "Faça o login novamente.",
        }).finally(() => {
          // Limpeza de sessão
          cache.clear();
          localStorage.removeItem("token");
          // zera header Authorization global
          if (api.defaults.headers?.common?.Authorization) {
            delete api.defaults.headers.common.Authorization;
          }

          // Cancela todas as requisições em voo para cortar a “chuva” de 401
          cancelAllRequests();
        });
      }
    } else if (
      error.response &&
      error.response.status === 400 &&
      !isSemTratGen
    ) {
      Swal.fire({
        icon: "warning",
        title: "Ação não realizada",
        text:
          error.response.data?.data?.[0] ??
          "O servidor não retornou a requisição. Tente novamente.",
      });
    }

    return Promise.reject(error);
  },
);

// ✅ helper para logout “limpo”
export async function logout() {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    // 1) pare pollers/intervals/sockets aqui se tiver
    // stopPollers();

    // 2) chama logout ainda autenticado (rota liberada acima)
    try {
      await api.post("/login/logoff");
    } catch (_) {
      /* ok se já expirou */
    }

    // 3) limpa credenciais
    cache.clear();
    localStorage.removeItem("token");
    if (api.defaults.headers?.common?.Authorization) {
      delete api.defaults.headers.common.Authorization;
    }

    // 4) cancela quaisquer requisições restantes
    cancelAllRequests();

    // 5) vai pro login
    router.push({ name: "UserLogin" });
  } finally {
    isLoggingOut = false;
  }
}

export default api;
export { cache };
